<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSubscription } from '@/hooks/useSubscription'
import { useAuth } from '@/hooks/useAuth'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const router = useRouter()
const { user } = useAuth()
const { isTrialing, isTrialExpired, trialEndsAt, subscriptionStatus } = useSubscription()

const isEmployeeOnly = computed(() => {
  const roles = Array.isArray(user.value?.roles) ? user.value.roles : []
  return roles.includes('employee') && !roles.includes('admin') && !roles.includes('manager')
})

// Live countdown
const now = ref(new Date())
let timer = null

onMounted(() => {
  timer = setInterval(() => {
    now.value = new Date()
  }, 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

const dismissed = ref(false)

const showBanner = computed(() =>
  !dismissed.value && !isEmployeeOnly.value && (isTrialing.value || isTrialExpired.value) && subscriptionStatus.value !== 'active'
)

const dismiss = () => {
  dismissed.value = true
}

const isExpired = computed(() => {
  if (isTrialExpired.value) return true
  if (!trialEndsAt.value) return false
  return now.value >= trialEndsAt.value
})

const timeRemaining = computed(() => {
  if (!trialEndsAt.value) return null
  const diff = trialEndsAt.value.getTime() - now.value.getTime()
  if (diff <= 0) return null

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return { days, hours, minutes, seconds }
})

const countdownText = computed(() => {
  if (!timeRemaining.value) return null
  const { days, hours, minutes, seconds } = timeRemaining.value

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  return `${minutes}m ${seconds}s`
})

const formattedTrialEnd = computed(() => {
  if (!trialEndsAt.value) return null
  return trialEndsAt.value.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
})

const goToBilling = () => {
  router.push('/app/settings/billing')
}
</script>

<template>
  <div
    v-if="showBanner"
    class="sticky top-0 z-40 w-full backdrop-blur-sm border-b px-4 py-2.5 flex items-center justify-center gap-3 text-sm relative"
    :class="isExpired
      ? 'bg-red-500/15 border-red-500/30 text-red-200'
      : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
    "
  >
    <!-- Trial active -->
    <template v-if="!isExpired">
      <span v-if="countdownText">
        {{ t('subscription.trial_banner', { days: timeRemaining?.days ?? 0 }, `You have ${timeRemaining?.days ?? 0} day(s) left in your free trial.`) }}
      </span>
      <span v-else>
        {{ t('subscription.trial_active_message', 'You are enjoying a free trial period.') }}
      </span>
      <span v-if="countdownText" class="font-mono font-semibold text-amber-300 tabular-nums">
        {{ countdownText }}
      </span>
      <button
        @click="goToBilling"
        class="ml-2 px-3 py-1 text-xs font-semibold rounded-md transition-colors bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 border border-amber-500/40"
      >
        {{ t('subscription.subscribe_now', 'Subscribe Now') }}
      </button>
    </template>

    <!-- Trial expired -->
    <template v-else>
      <span>
        {{ t('subscription.trial_ended_banner', 'Your trial period has ended. Subscribe to continue using the service.') }}
      </span>
      <button
        @click="goToBilling"
        class="ml-2 px-3 py-1 text-xs font-semibold rounded-md transition-colors bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/40"
      >
        {{ t('subscription.go_to_billing', 'Go to Billing') }}
      </button>
    </template>

    <!-- Close button -->
    <button
      @click="dismiss"
      class="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md opacity-60 hover:opacity-100 transition-opacity"
      :class="isExpired ? 'hover:bg-red-500/20' : 'hover:bg-amber-500/20'"
    >
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>
